import React, { useState } from 'react'
import { Modal, Button, Typography, Space, Steps } from 'antd'
import {
  AppstoreOutlined,
  ScanOutlined,
  ThunderboltOutlined,
  WarningOutlined,
  LeftOutlined,
  RightOutlined,
  SoundOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'

const { Title, Paragraph, Text } = Typography

// 格子元件
function SlotCell({ number, status, highlight }) {
  const styles = {
    empty: { background: '#fafafa', border: '1px dashed #d9d9d9', color: '#bbb' },
    active: { background: '#e6f7ff', border: '1px solid #91d5ff', color: '#1890ff' },
    done: { background: '#f6ffed', border: '1px solid #b7eb8f', color: '#52c41a' },
    exception: { background: '#fff7e6', border: '1px solid #ffd591', color: '#fa8c16' },
  }
  const s = styles[status] || styles.empty

  return (
    <div style={{
      ...s,
      borderRadius: 8,
      padding: '10px 0',
      textAlign: 'center',
      fontWeight: 600,
      fontSize: 16,
      position: 'relative',
      boxShadow: highlight ? '0 0 0 2px #1890ff' : 'none',
      transition: 'all 0.2s',
    }}>
      <div style={{ fontSize: 20 }}>{number}</div>
      {status === 'done' && <div style={{ fontSize: 11, marginTop: 2 }}>✓</div>}
      {status === 'active' && <div style={{ fontSize: 11, marginTop: 2 }}>📦</div>}
      {status === 'exception' && <div style={{ fontSize: 11, marginTop: 2 }}>⚠</div>}
    </div>
  )
}

// Ch1 圖解：掃描商品 → 語音播報 → 放入格子
function Ch1Diagram({ t, prefix }) {
  return (
    <div style={{ background: '#f6f8fa', borderRadius: 8, padding: '20px 24px', margin: '16px 0' }}>
      {/* 流程 */}
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
          <span style={{ background: '#fff', border: '1px solid #d9d9d9', borderRadius: 6, padding: '4px 12px', fontSize: 13 }}>
            {t(`${prefix}.ch1_flow_scan`)}
          </span>
          <ArrowDownOutlined style={{ color: '#999', transform: 'rotate(-90deg)' }} />
          <span style={{ background: '#e6f7ff', border: '1px solid #91d5ff', borderRadius: 6, padding: '4px 12px', fontSize: 13, color: '#1890ff' }}>
            <SoundOutlined /> {t(`${prefix}.ch1_flow_voice`)}
          </span>
          <ArrowDownOutlined style={{ color: '#999', transform: 'rotate(-90deg)' }} />
          <span style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6, padding: '4px 12px', fontSize: 13, color: '#52c41a' }}>
            {t(`${prefix}.ch1_flow_place`)}
          </span>
        </div>
      </div>

      {/* 格子架 */}
      <div style={{ fontSize: 12, color: '#999', marginBottom: 8, textAlign: 'center' }}>
        {t(`${prefix}.ch1_grid_label`)}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, maxWidth: 320, margin: '0 auto' }}>
        <SlotCell number={1} status="empty" />
        <SlotCell number={2} status="empty" />
        <SlotCell number={3} status="active" highlight />
        <SlotCell number={4} status="empty" />
      </div>
    </div>
  )
}

// Ch3 圖解：正常訂單 + 異常品混合格子
function Ch3Diagram({ t, prefix }) {
  return (
    <div style={{ background: '#f6f8fa', borderRadius: 8, padding: '20px 24px', margin: '16px 0' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, maxWidth: 360, margin: '0 auto' }}>
        <SlotCell number={1} status="done" />
        <SlotCell number={2} status="active" />
        <SlotCell number={3} status="active" />
        <SlotCell number={4} status="empty" />
        <SlotCell number={5} status="empty" />
        <SlotCell number={6} status="exception" />
        <SlotCell number={7} status="exception" />
        <SlotCell number={8} status="empty" />
      </div>

      {/* 圖例 */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 12, flexWrap: 'wrap' }}>
        {[
          { color: '#52c41a', border: '#b7eb8f', bg: '#f6ffed', label: t(`${prefix}.ch3_legend_done`) },
          { color: '#1890ff', border: '#91d5ff', bg: '#e6f7ff', label: t(`${prefix}.ch3_legend_active`) },
          { color: '#fa8c16', border: '#ffd591', bg: '#fff7e6', label: t(`${prefix}.ch3_legend_exception`) },
        ].map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
            <div style={{ width: 12, height: 12, borderRadius: 3, background: item.bg, border: `1px solid ${item.border}` }} />
            <span style={{ color: '#666' }}>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * 配貨作業概念說明 — 故事書風格 Wizard Modal（Operator 專用）
 */
export default function DistributeConceptModal({ visible, onClose }) {
  const { t } = useTranslation()
  const [currentStep, setCurrentStep] = useState(0)

  const prefix = 'concepts.distribute'

  const chapters = [
    {
      icon: <AppstoreOutlined style={{ fontSize: 28, color: '#1890ff' }} />,
      titleKey: `${prefix}.ch1_title`,
      paragraphs: [`${prefix}.ch1_p1`, `${prefix}.ch1_p2`],
      customDiagram: <Ch1Diagram t={t} prefix={prefix} />,
    },
    {
      icon: <ScanOutlined style={{ fontSize: 28, color: '#52c41a' }} />,
      titleKey: `${prefix}.ch2_title`,
      paragraphs: [`${prefix}.ch2_p1`, `${prefix}.ch2_p2`],
      bullets: [`${prefix}.ch2_b1`, `${prefix}.ch2_b2`, `${prefix}.ch2_b3`],
    },
    {
      icon: <WarningOutlined style={{ fontSize: 28, color: '#faad14' }} />,
      titleKey: `${prefix}.ch3_title`,
      paragraphs: [`${prefix}.ch3_p1`, `${prefix}.ch3_p2`],
      customDiagram: <Ch3Diagram t={t} prefix={prefix} />,
      paragraphsAfter: [`${prefix}.ch3_p3`],
    },
    {
      icon: <ThunderboltOutlined style={{ fontSize: 28, color: '#722ed1' }} />,
      titleKey: `${prefix}.ch4_title`,
      paragraphs: [`${prefix}.ch4_p1`],
      bullets: [`${prefix}.ch4_b1`, `${prefix}.ch4_b2`, `${prefix}.ch4_b3`],
      paragraphsAfter: [`${prefix}.ch4_p2`],
    },
  ]

  const chapter = chapters[currentStep]
  const isFirst = currentStep === 0
  const isLast = currentStep === chapters.length - 1

  const handleClose = () => {
    setCurrentStep(0)
    onClose()
  }

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      closable={false}
      width={720}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {currentStep + 1} / {chapters.length}
          </Text>
          <Space>
            {!isFirst && (
              <Button icon={<LeftOutlined />} onClick={() => setCurrentStep(s => s - 1)}>
                {t(`${prefix}.prev`)}
              </Button>
            )}
            {isLast ? (
              <Button type="primary" onClick={handleClose}>
                {t(`${prefix}.got_it`)}
              </Button>
            ) : (
              <Button type="primary" onClick={() => setCurrentStep(s => s + 1)}>
                {t(`${prefix}.next`)} <RightOutlined />
              </Button>
            )}
          </Space>
        </div>
      }
    >
      <Steps
        current={currentStep}
        size="small"
        onChange={(step) => setCurrentStep(step)}
        style={{ marginBottom: 32 }}
        items={chapters.map((ch, i) => ({
          title: i === currentStep ? t(ch.titleKey) : '',
        }))}
      />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 24 }}>
        {chapter.icon}
        <Title level={3} style={{ margin: 0 }}>
          {t(chapter.titleKey)}
        </Title>
      </div>

      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        {chapter.paragraphs?.map((key, i) => (
          <Paragraph key={i} style={{ fontSize: 15, lineHeight: 1.8, color: '#333' }}>
            {t(key)}
          </Paragraph>
        ))}

        {chapter.bullets && (
          <ul style={{ paddingLeft: 20, marginBottom: 16 }}>
            {chapter.bullets.map((key, i) => (
              <li key={i} style={{ fontSize: 15, lineHeight: 2, color: '#333' }}>
                {t(key)}
              </li>
            ))}
          </ul>
        )}

        {chapter.customDiagram}

        {chapter.diagram && (
          <div style={{
            background: '#f6f8fa',
            border: '1px solid #e8e8e8',
            borderRadius: 8,
            padding: '20px 24px',
            margin: '16px 0',
            fontFamily: 'monospace',
            fontSize: 14,
            lineHeight: 1.8,
            whiteSpace: 'pre-line',
          }}>
            {t(chapter.diagram)}
          </div>
        )}

        {chapter.paragraphsAfter?.map((key, i) => (
          <Paragraph key={`after-${i}`} style={{ fontSize: 15, lineHeight: 1.8, color: '#333' }}>
            {t(key)}
          </Paragraph>
        ))}
      </div>
    </Modal>
  )
}
